// Team Capacity — Obsidian-style force-directed knowledge graph.
//
// The intent: surface every meaningful relationship at once so the
// chairman / ManCo can see clusters, hubs, and overlaps the way an
// Obsidian vault makes you see a body of work.
//
// Node types (each visibly labelled):
//   PERSON    — every contributor we know about (action owners, doc
//               uploaders, note authors). Size scales with total load.
//   KPI       — the 7 canonical KPIs (S1-S3, D1-D3, SYM).
//   CATEGORY  — the must-win category substrate (fee-defense, card,
//               gift-card, mobile, utility-bills, transport, groceries,
//               pharmacies, tag-to-tag, gaming, other). Only categories
//               actually touched by data render.
//   DOCUMENT  — every doc parsed into the team library, labelled with
//               its filename (truncated).
//
// Edges (the WHY of who's connected to what):
//   person—KPI         action ownership against that KPI
//   person—category    action with explicit category impact (s1:card etc)
//   KPI—category       every action linking a KPI to a category implies
//                      a system edge — these light grey lines surface
//                      which categories are actually live on which KPIs
//   person—document    uploader
//   person—KPI (faint) author of a per-KPI note
//   document—category  filename keyword match (cheap heuristic — full-
//                      text matching against the Worker's Vectorize index
//                      is the right next step)
//
// Layout: a small bespoke force simulation (Coulomb-like repulsion,
// edge springs, centre gravity, cooling) runs once after data loads to
// converge node positions into organic clusters. Once converged, react-
// flow takes over for pan / zoom / drag.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import {
  RadialCapacity,
  buildRadialData,
} from "../components/team-capacity/RadialCapacity";
import { listActions, type ActionRow, type ActionStatus } from "../lib/actions";
import { CATEGORIES, type Category } from "../lib/categories";
import { fetchDocuments, type DocSummary } from "../lib/documents-sync";
import { KPI_BUNDLE } from "../lib/kpis";
import { listNotes, type Note } from "../lib/notes";
import {
  DEMAND_KPIS,
  PLATFORM_TRACKER_TASKS,
  SUPPLY_KPIS,
  type KpiBrief,
  type KpiTrackOwner,
} from "../lib/partner-rollout";

// ---------------------------------------------------------------------------
// Normalisation — owner strings arrive in many shapes; collapse to a
// stable display name we can use as the person-node key.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Static workstream seed.
//
// The action ledger only holds commitments people have explicitly logged
// via the in-app editor. The Bvndle ManCo's actual working surface is
// much wider — there are 30+ items in the Product Priority tracker, 11 in
// the Tasks Tracker, plus partner-category leads — most of which are
// tracked in xlsx, not the ledger.
//
// We synthesise these as if they were commitments so the graph reflects
// "what people are actually working on" rather than "what's been
// formally committed in-app." Synthetic ids are deterministic so they
// dedupe across refreshes; status maps roughly:
//
//   done / live       → status: "done"
//   wip / testing     → status: "doing"
//   pending / not-started → status: "watch"
//   blocked / stalled → status: "blocked"
//
// When someone DOES commit one of these explicitly via the editor, both
// the synthetic seed and the live action coexist as separate edges in
// the graph — the live one carries the editor metadata (due-by, blocker
// text). For the heavy-load tally we count each person once per distinct
// (owner, impact) pair via the action-graph's dedup logic upstream.

const STATIC_STATUS_MAP: Record<string, ActionStatus> = {
  done: "done",
  live: "done",
  wip: "doing",
  testing: "doing",
  pending: "watch",
  "not-started": "watch",
  blocked: "blocked",
};

function staticAction(
  id: string,
  owner: string,
  title: string,
  impacts: string[],
  status: ActionStatus,
): ActionRow {
  return {
    id,
    title,
    impacts,
    owner,
    status,
    blockers: "",
    doing: "",
    dueBy: "",
    createdAt: "2026-06-01T00:00:00Z",
    author: "system",
    kind: "commitment",
    synced: true,
  };
}

function slugifyPart(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24);
}

function buildStaticWorkstreams(): ActionRow[] {
  const out: ActionRow[] = [];

  const collectFromKpi = (kpi: KpiBrief) => {
    const kpiId = kpi.code.toLowerCase(); // "S1" → "s1"
    for (const to of kpi.trackOwners) {
      const id = `sw-kpi-${kpiId}-${slugifyPart(to.name)}-${slugifyPart(to.scope)}`;
      out.push(
        staticAction(
          id,
          to.name,
          `${kpi.code} · ${to.scope}`,
          [kpiId],
          STATIC_STATUS_MAP[to.status] ?? "doing",
        ),
      );
    }
  };
  for (const k of SUPPLY_KPIS) collectFromKpi(k);
  for (const k of DEMAND_KPIS) collectFromKpi(k);

  // Platform-wide tracker tasks (UBA/MTN readiness, app revamp, etc.) —
  // attach to SYM since they affect the whole coin economy.
  for (const t of PLATFORM_TRACKER_TASKS) {
    out.push(
      staticAction(
        `sw-platform-${t.number}`,
        t.owner,
        `Tracker #${t.number} · ${t.title}`,
        ["sym"],
        STATIC_STATUS_MAP[t.status] ?? "doing",
      ),
    );
  }

  // Partner-category leads from the rewritten categories.ts. Each
  // category has at least one named lead — strategically they carry
  // dual responsibility: getting merchants onboarded so they can
  // ISSUE coins (S1) AND driving redemption volume on them (D1). So
  // each lead emits TWO impact rows (one on each side) instead of one
  // on D1. Without this, the team-capacity radial view inevitably
  // reads demand-heavy because the supply half is unrepresented.
  for (const cat of CATEGORIES) {
    if (!cat.owner) continue;
    out.push(
      staticAction(
        `sw-cat-s1-${cat.code}`,
        cat.owner,
        `${cat.label} · merchant onboarding`,
        [`s1:${cat.code}`],
        "doing",
      ),
    );
    out.push(
      staticAction(
        `sw-cat-d1-${cat.code}`,
        cat.owner,
        `${cat.label} · redemption volume`,
        [`d1:${cat.code}`],
        "doing",
      ),
    );
  }

  return out;
}

// ---------------------------------------------------------------------------

function normalisePerson(raw: string): string {
  if (!raw) return "";
  const at = raw.indexOf("@");
  let s = at >= 0 ? raw.slice(0, at) : raw;
  s = s.split(/[·+\/,]|\sand\s/)[0] ?? s;
  s = s.replace(/\([^)]*\)/g, "");
  s = s.replace(/[._-]/g, " ").trim();
  if (!s) return "";
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// ---------------------------------------------------------------------------
// Doc → category keyword matching. Cheap. Full-text retrieval through
// the Worker's Vectorize index would be sharper but adds a network call
// per doc; defer.
// ---------------------------------------------------------------------------

const CATEGORY_KEYWORDS: Record<string, string[]> = {
  "fee-defense": ["fee", "wedge", "p2p", "transfer", "vbank"],
  "card": ["card", "uba", "visa", "issuer"],
  "gift-card": ["gift", "giftcard", "scribbles", "regal"],
  "mobile": ["mobile", "mtn", "airtime", "data", "glo"],
  "utility-bills": ["utility", "bills", "electricity", "nepa", "dstv"],
  "transport": ["transport", "bvndlego", "bvndle-go", "ride", "ardova", "nepal", "fuel"],
  "groceries": ["grocer", "supermarket", "jendol", "market square", "trolley"],
  "pharmacies": ["pharma", "medplus", "pharmacy"],
  "tag-to-tag": ["tag-to-tag", "tag to tag", "peer"],
  "gaming": ["gaming", "game", "betting"],
};

function docMatchesCategory(fileName: string, code: string): boolean {
  const fn = fileName.toLowerCase();
  const kws = CATEGORY_KEYWORDS[code];
  if (!kws) return false;
  for (const kw of kws) if (fn.includes(kw)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

type NodeKind = "person" | "kpi" | "category" | "document";

interface GNode {
  id: string;
  kind: NodeKind;
  label: string;
  /** Display tag for KPIs (the code "S1", "D2"…). */
  sub?: string;
  /** Visual weight: more connections → bigger node. */
  degree: number;
  /** Side-tinting for KPI nodes. */
  side?: "supply" | "demand" | "sym";
  /** Filename kind for document nodes. */
  docKind?: string;
  /** Person load breakdown (only for person nodes). */
  load?: { commitments: number; documents: number; notes: number };
  /** Live position after simulation. */
  x: number;
  y: number;
}

interface GEdge {
  id: string;
  source: string;
  target: string;
  /** Edge category — drives styling. */
  rel:
    | "person-kpi"
    | "person-cat"
    | "kpi-cat"
    | "person-doc"
    | "person-kpi-note"
    | "doc-cat";
  /** True when the underlying commitment is blocked. */
  blocked?: boolean;
  /** True when commitment is in flight. */
  live?: boolean;
}

interface GraphData {
  nodes: GNode[];
  edges: GEdge[];
  heavyLoad: GNode[]; // people with 3+ items
  topOverlaps: { node: GNode; ownerCount: number; owners: string[] }[]; // KPIs with 2+ owners
  totals: { people: number; actions: number; docs: number; notes: number };
}

// ---------------------------------------------------------------------------
// Build the graph from raw data
// ---------------------------------------------------------------------------

function buildGraph(
  actions: ActionRow[],
  docs: DocSummary[],
  notes: Note[],
): GraphData {
  const peopleMap = new Map<string, GNode>();
  const kpiMap = new Map<string, GNode>();
  const catMap = new Map<string, GNode>();
  const docMap = new Map<string, GNode>();
  const kpiOwners = new Map<string, Set<string>>();
  const edges: GEdge[] = [];

  // Seed all KPI nodes so empty KPIs still appear (their absence of edges
  // is the chairman signal).
  for (const k of KPI_BUNDLE.kpis) {
    kpiMap.set(k.id, {
      id: `k-${k.id}`,
      kind: "kpi",
      label: k.title.replace(/^Must-win partners onboarded · /, ""),
      sub: k.code,
      side: k.side,
      degree: 0,
      x: 0,
      y: 0,
    });
    kpiOwners.set(k.id, new Set());
  }

  const personFor = (raw: string): GNode | null => {
    const key = normalisePerson(raw);
    if (!key) return null;
    let p = peopleMap.get(key);
    if (!p) {
      p = {
        id: `p-${key}`,
        kind: "person",
        label: key,
        degree: 0,
        load: { commitments: 0, documents: 0, notes: 0 },
        x: 0,
        y: 0,
      };
      peopleMap.set(key, p);
    }
    return p;
  };
  const categoryFor = (code: string): GNode | null => {
    if (!code) return null;
    let c = catMap.get(code);
    if (!c) {
      const meta = CATEGORIES.find((x: Category) => x.code === code);
      c = {
        id: `c-${code}`,
        kind: "category",
        label: meta?.label ?? code,
        degree: 0,
        x: 0,
        y: 0,
      };
      catMap.set(code, c);
    }
    return c;
  };

  const kpiCatEdgeSeen = new Set<string>();
  const personCatEdgeSeen = new Set<string>();

  // ---- Actions: the spine of accountability ----
  for (const a of actions) {
    const p = personFor(a.owner);
    if (!p) continue;
    p.load!.commitments += 1;
    p.degree += 1;

    const kpisTouched = new Set<string>();
    const catsTouched = new Set<string>();
    for (const imp of a.impacts ?? []) {
      const [kpiId, catCode] = imp.split(":");
      if (kpiId) kpisTouched.add(kpiId);
      if (catCode) catsTouched.add(catCode);
    }

    const live = a.status === "doing" || a.status === "done";
    const blocked = a.status === "blocked";

    // Person → KPI
    for (const kpiId of kpisTouched) {
      const k = kpiMap.get(kpiId);
      if (!k) continue;
      kpiOwners.get(kpiId)!.add(p.id);
      k.degree += 1;
      edges.push({
        id: `e-act-pk-${a.id}-${p.id}-${kpiId}`,
        source: p.id,
        target: k.id,
        rel: "person-kpi",
        live,
        blocked,
      });
    }
    // Person → Category (deduped per person+cat to keep the graph clean)
    for (const catCode of catsTouched) {
      const c = categoryFor(catCode);
      if (!c) continue;
      const key = `${p.id}--${c.id}`;
      if (!personCatEdgeSeen.has(key)) {
        personCatEdgeSeen.add(key);
        c.degree += 1;
        edges.push({
          id: `e-pc-${p.id}-${c.id}`,
          source: p.id,
          target: c.id,
          rel: "person-cat",
          live,
          blocked,
        });
      }
    }
    // KPI → Category — system edge surfacing which categories are live
    // on which KPIs across the action ledger.
    for (const kpiId of kpisTouched) {
      for (const catCode of catsTouched) {
        const k = kpiMap.get(kpiId);
        const c = categoryFor(catCode);
        if (!k || !c) continue;
        const key = `${k.id}--${c.id}`;
        if (!kpiCatEdgeSeen.has(key)) {
          kpiCatEdgeSeen.add(key);
          edges.push({
            id: `e-kc-${k.id}-${c.id}`,
            source: k.id,
            target: c.id,
            rel: "kpi-cat",
          });
        }
      }
    }
  }

  // ---- Documents: who shared what, plus keyword-match to categories ----
  for (const d of docs) {
    const p = personFor(d.uploadedBy ?? "");
    const docNode: GNode = {
      id: `d-${d.id}`,
      kind: "document",
      label: truncateName(d.fileName),
      docKind: d.kind,
      degree: 0,
      x: 0,
      y: 0,
    };
    docMap.set(d.id, docNode);

    if (p) {
      p.load!.documents += 1;
      p.degree += 1;
      docNode.degree += 1;
      edges.push({
        id: `e-pd-${p.id}-${d.id}`,
        source: p.id,
        target: docNode.id,
        rel: "person-doc",
      });
    }
    // Doc → category by filename keyword match — cheap heuristic.
    for (const cat of CATEGORIES) {
      if (docMatchesCategory(d.fileName, cat.code)) {
        const c = categoryFor(cat.code);
        if (!c) continue;
        c.degree += 1;
        docNode.degree += 1;
        edges.push({
          id: `e-dc-${d.id}-${c.id}`,
          source: docNode.id,
          target: c.id,
          rel: "doc-cat",
        });
      }
    }
  }

  // ---- Notes: weak signal of attention; thin lines so they don't drown ----
  for (const n of notes) {
    const p = personFor(n.author);
    if (!p) continue;
    p.load!.notes += 1;
    p.degree += 0.4;
    if (n.kpiId) {
      const k = kpiMap.get(n.kpiId);
      if (k) {
        k.degree += 0.4;
        edges.push({
          id: `e-pkn-${n.id}-${p.id}-${n.kpiId}`,
          source: p.id,
          target: k.id,
          rel: "person-kpi-note",
        });
      }
    }
  }

  const nodes: GNode[] = [
    ...peopleMap.values(),
    ...kpiMap.values(),
    ...catMap.values(),
    ...docMap.values(),
  ];

  // Drop categories with no edges — they're seeded only on demand anyway,
  // but in case any leak through (no data) we still don't want orphans.
  const connected = new Set<string>();
  for (const e of edges) {
    connected.add(e.source);
    connected.add(e.target);
  }
  const trimmed = nodes.filter(
    (n) => n.kind === "kpi" || n.kind === "person" || connected.has(n.id),
  );

  // Heavy load: 3+ items.
  const heavyLoad = [...peopleMap.values()]
    .filter((p) => totalLoad(p) >= 3)
    .sort((a, b) => totalLoad(b) - totalLoad(a));

  // Top overlaps.
  const topOverlaps = [...kpiOwners.entries()]
    .filter(([, owners]) => owners.size >= 2)
    .map(([id, owners]) => {
      const node = kpiMap.get(id)!;
      return {
        node,
        ownerCount: owners.size,
        owners: [...owners].map((pid) =>
          peopleMap.get(pid.replace(/^p-/, ""))?.label ?? pid,
        ),
      };
    })
    .sort((a, b) => b.ownerCount - a.ownerCount);

  return {
    nodes: trimmed,
    edges,
    heavyLoad,
    topOverlaps,
    totals: {
      people: peopleMap.size,
      actions: actions.length,
      docs: docs.length,
      notes: notes.length,
    },
  };
}

function totalLoad(p: GNode): number {
  return (p.load?.commitments ?? 0) + (p.load?.documents ?? 0) + (p.load?.notes ?? 0);
}

function truncateName(s: string, max = 28): string {
  if (s.length <= max) return s;
  const ext = s.lastIndexOf(".");
  if (ext > 0 && s.length - ext < 6) {
    return s.slice(0, max - (s.length - ext) - 1) + "…" + s.slice(ext);
  }
  return s.slice(0, max - 1) + "…";
}

// ---------------------------------------------------------------------------
// Force-directed layout — small bespoke simulation. Coulomb-like
// repulsion between every pair, edge springs with edge-type-specific
// rest length, gentle centre gravity, simulated annealing via cooling.
// ---------------------------------------------------------------------------

interface SimPos {
  x: number; y: number; vx: number; vy: number; mass: number;
}

function massFor(n: GNode): number {
  if (n.kind === "kpi") return 6;
  if (n.kind === "person") return 4 + Math.min(8, n.degree * 0.4);
  if (n.kind === "category") return 3;
  return 1.5;
}

function restLengthFor(rel: GEdge["rel"]): number {
  switch (rel) {
    case "person-kpi": return 130;
    case "person-cat": return 110;
    case "kpi-cat": return 90;
    case "person-doc": return 80;
    case "person-kpi-note": return 150;
    case "doc-cat": return 70;
  }
}

function runForceLayout(
  nodes: GNode[],
  edges: GEdge[],
  opts: { width: number; height: number; iterations?: number } = { width: 1400, height: 900 },
): void {
  const ITER = opts.iterations ?? 360;
  const cx = opts.width / 2;
  const cy = opts.height / 2;

  // Initialise positions — small jitter around centre so the sim has
  // a starting "fog".
  const sim: SimPos[] = nodes.map(() => ({
    x: cx + (Math.random() - 0.5) * 220,
    y: cy + (Math.random() - 0.5) * 220,
    vx: 0,
    vy: 0,
    mass: 1,
  }));
  for (let i = 0; i < nodes.length; i++) sim[i]!.mass = massFor(nodes[i]!);

  const idx = new Map<string, number>();
  nodes.forEach((n, i) => idx.set(n.id, i));

  const REPULSE = 4500;
  const SPRING = 0.04;
  const GRAVITY = 0.012;
  const DAMP = 0.78;

  for (let step = 0; step < ITER; step++) {
    const cool = Math.max(0.18, 1 - step / ITER);

    // Pairwise repulsion (Coulomb-like). O(n²) but n is small.
    for (let i = 0; i < sim.length; i++) {
      const a = sim[i]!;
      for (let j = i + 1; j < sim.length; j++) {
        const b = sim[j]!;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d2 = Math.max(64, dx * dx + dy * dy);
        const d = Math.sqrt(d2);
        const f = (REPULSE * (a.mass + b.mass) * 0.5) / d2;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Edge springs.
    for (const e of edges) {
      const ai = idx.get(e.source);
      const bi = idx.get(e.target);
      if (ai == null || bi == null) continue;
      const a = sim[ai]!;
      const b = sim[bi]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      const rest = restLengthFor(e.rel);
      const f = (d - rest) * SPRING;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // Centre gravity + integrate.
    for (const s of sim) {
      s.vx -= (s.x - cx) * GRAVITY;
      s.vy -= (s.y - cy) * GRAVITY;
      s.x += s.vx * cool;
      s.y += s.vy * cool;
      s.vx *= DAMP;
      s.vy *= DAMP;
    }
  }

  // Write back final positions.
  for (let i = 0; i < nodes.length; i++) {
    nodes[i]!.x = sim[i]!.x;
    nodes[i]!.y = sim[i]!.y;
  }
}

// ---------------------------------------------------------------------------
// React-flow translation
// ---------------------------------------------------------------------------

function toFlow(graph: GraphData): { nodes: Node[]; edges: Edge[] } {
  const flowNodes: Node[] = graph.nodes.map((g) => {
    const radius = radiusFor(g);
    // Bounding box ≈ circle + label below.
    const w = Math.max(radius * 2, labelWidth(g));
    const h = radius * 2 + 22;
    return {
      id: g.id,
      type: g.kind,
      position: { x: g.x - w / 2, y: g.y - radius },
      data: { node: g, radius, w, h },
      style: { width: w, height: h },
      draggable: true,
    };
  });

  const flowEdges: Edge[] = graph.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    // Straight edges feel more like an Obsidian knowledge-graph than the
    // default bezier curves — direct lines from circle to circle.
    type: "straight",
    style: edgeStyle(e),
    animated: e.live && e.rel === "person-kpi" && !e.blocked,
    interactionWidth: 6,
  }));

  return { nodes: flowNodes, edges: flowEdges };
}

function radiusFor(g: GNode): number {
  if (g.kind === "kpi") return 22;
  if (g.kind === "person") return 14 + Math.min(14, g.degree * 0.9);
  if (g.kind === "category") return 10;
  return 8;
}

function labelWidth(g: GNode): number {
  return Math.max(40, Math.min(180, g.label.length * 6.5));
}

function edgeStyle(e: GEdge): React.CSSProperties {
  // Color picks: green = live commitment work; red = blocked; teal = the
  // standing "what's connected to what" system layer; amber = doc→cat;
  // grey = notes / soft signals. Opacities tuned to read against the
  // dark canvas — too low and edges vanish, too high and the graph
  // becomes spaghetti.
  if (e.blocked) {
    return { stroke: "#e07474", strokeWidth: 1.8, opacity: 1 };
  }
  switch (e.rel) {
    case "person-kpi":
      return { stroke: "#7ad19f", strokeWidth: 1.6, opacity: 0.85 };
    case "person-cat":
      return { stroke: "#5ec98c", strokeWidth: 1.3, opacity: 0.7 };
    case "kpi-cat":
      return { stroke: "#5fb0d4", strokeWidth: 1.1, opacity: 0.6, strokeDasharray: "4 4" };
    case "person-doc":
      return { stroke: "#9a9aaa", strokeWidth: 1, opacity: 0.55, strokeDasharray: "3 4" };
    case "person-kpi-note":
      return { stroke: "#7a7a8a", strokeWidth: 0.8, opacity: 0.4, strokeDasharray: "1 5" };
    case "doc-cat":
      return { stroke: "#e6b25a", strokeWidth: 1.2, opacity: 0.7 };
  }
}

// ---------------------------------------------------------------------------
// Custom nodes (every node visibly labelled, label SITS BELOW the dot)
// ---------------------------------------------------------------------------

interface NodeData {
  node: GNode;
  radius: number;
  w: number;
  h: number;
}

function PersonNode({ data }: NodeProps) {
  const d = data as unknown as NodeData;
  const g = d.node;
  const radius = d.radius;
  return (
    <div style={nodeOuter(d)}>
      <CentreHandles radius={radius} />
      <div
        style={{
          width: radius * 2,
          height: radius * 2,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, #b4a8ff 0%, #6f63d6 55%, #4f47a3 100%)",
          boxShadow: `0 0 ${radius * 1.5}px rgba(140,120,255,0.35), 0 2px 8px rgba(0,0,0,0.5)`,
          border: "1.5px solid rgba(255,255,255,0.25)",
        }}
        title={`${g.label} · ${g.load?.commitments ?? 0} commitments · ${g.load?.documents ?? 0} docs · ${g.load?.notes ?? 0} notes`}
      />
      <NodeLabel>{g.label}</NodeLabel>
    </div>
  );
}

function KpiNode({ data }: NodeProps) {
  const d = data as unknown as NodeData;
  const g = d.node;
  const radius = d.radius;
  const sideColor =
    g.side === "supply" ? "#534ab7" : g.side === "demand" ? "#d96b9c" : "#8a8a85";
  return (
    <div style={nodeOuter(d)}>
      <CentreHandles radius={radius} />
      <div
        title={`${g.sub} · ${g.label}`}
        style={{
          width: radius * 2,
          height: radius * 2,
          borderRadius: "50%",
          background: sideColor,
          boxShadow: `0 0 ${radius * 1.5}px ${sideColor}55, 0 2px 8px rgba(0,0,0,0.45)`,
          border: "1.5px solid rgba(255,255,255,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.8,
        }}
      >
        {g.sub}
      </div>
      <NodeLabel>{g.label}</NodeLabel>
    </div>
  );
}

function CategoryNode({ data }: NodeProps) {
  const d = data as unknown as NodeData;
  const g = d.node;
  const radius = d.radius;
  return (
    <div style={nodeOuter(d)}>
      <CentreHandles radius={radius} />
      <div
        title={g.label}
        style={{
          width: radius * 2,
          height: radius * 2,
          borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #9be6b8, #4fa974)",
          boxShadow: "0 0 12px rgba(94,201,140,0.4)",
          border: "1px solid rgba(255,255,255,0.2)",
        }}
      />
      <NodeLabel small>{g.label}</NodeLabel>
    </div>
  );
}

function DocumentNode({ data }: NodeProps) {
  const d = data as unknown as NodeData;
  const g = d.node;
  const radius = d.radius;
  const kindColors: Record<string, string> = {
    pdf: "#e88080", docx: "#7fbbf0", xlsx: "#9be6b8", csv: "#9be6b8",
    pptx: "#f0c279", html: "#caa1f0", image: "#80c8f0", txt: "#bababa",
  };
  const color = kindColors[g.docKind ?? "txt"] ?? "#d4b876";
  return (
    <div style={nodeOuter(d)}>
      <CentreHandles radius={radius} />
      <div
        title={g.label}
        style={{
          width: radius * 2,
          height: radius * 2,
          borderRadius: "50%",
          background: color,
          boxShadow: `0 0 8px ${color}66`,
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      />
      <NodeLabel small>{g.label}</NodeLabel>
    </div>
  );
}

function nodeOuter(d: NodeData): React.CSSProperties {
  return {
    width: d.w,
    height: d.h,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    pointerEvents: "all",
    position: "relative",
  };
}

/** Invisible handles anchored at the visual centre of the circle so edges
 * route through the dot. React Flow won't render any edge unless its
 * source AND target nodes expose a Handle — without these, the whole
 * edge layer silently disappears. */
function CentreHandles({ radius }: { radius: number }) {
  // We anchor at top-of-circle-+-radius so the handle sits in the centre
  // of the dot. The label sits below, so this also keeps edges from
  // routing through the label text.
  const cx = "50%";
  const cy = radius;
  const common: React.CSSProperties = {
    width: 1,
    height: 1,
    border: "none",
    background: "transparent",
    opacity: 0,
    pointerEvents: "none",
    left: cx,
    top: cy,
  };
  return (
    <>
      <Handle id="t" type="target" position={Position.Top} style={common} isConnectable={false} />
      <Handle id="s" type="source" position={Position.Top} style={common} isConnectable={false} />
    </>
  );
}

function NodeLabel({ children, small }: { children: React.ReactNode; small?: boolean }) {
  return (
    <div
      style={{
        marginTop: 5,
        fontSize: small ? 9.5 : 10.5,
        fontWeight: 500,
        color: "#dcdce8",
        textAlign: "center",
        textShadow: "0 1px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.7)",
        whiteSpace: "nowrap",
        maxWidth: 180,
        overflow: "hidden",
        textOverflow: "ellipsis",
        letterSpacing: 0.15,
      }}
    >
      {children}
    </div>
  );
}

const NODE_TYPES = {
  person: PersonNode,
  kpi: KpiNode,
  category: CategoryNode,
  document: DocumentNode,
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CANVAS_W = 1400;
const CANVAS_H = 900;

export function TeamCapacityPage() {
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);

  // load = pull the three live data sources (action ledger, document
  // library, notes store) and merge them with the static workstream seed
  // derived from partner-rollout.ts so the graph has the full ManCo
  // working surface, not just whatever's been explicitly committed via
  // the in-app commitment editor.
  //
  // `silent` skips the "Loading…" flicker — used by the auto-refresh
  // timer so the page updates quietly on a cadence without UI thrash.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [a, d, n] = await Promise.all([
        listActions().catch(() => [] as ActionRow[]),
        fetchDocuments().catch(() => [] as DocSummary[]),
        listNotes().catch(() => [] as Note[]),
      ]);
      // Static seed from the trackers — every named track owner in
      // SUPPLY_KPIS / DEMAND_KPIS, every platform tracker task, plus
      // category leads from categories.ts. Stable synthetic ids prevent
      // duplication if the same person also has a live commitment.
      const seeded = buildStaticWorkstreams();
      setActions([...seeded, ...a]);
      setDocs(d);
      setNotes(n);
      setRefreshedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Auto-refresh on a 30s cadence — same pattern S/D blocks follow
    // when their underlying data shifts. Silent updates keep the graph
    // alive without UI flicker.
    const id = window.setInterval(() => void load(true), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  // Build + lay out the graph. Force sim runs once per refresh so the
  // organic clusters are stable across pan / zoom / drag.
  const graph = useMemo(() => {
    const g = buildGraph(actions, docs, notes);
    runForceLayout(g.nodes, g.edges, { width: CANVAS_W, height: CANVAS_H });
    return g;
  }, [actions, docs, notes]);

  // Radial data — flattened (person, kpi, category, active) tuples
  // derived from the same actions list. The radial view treats every
  // active commitment as a unit of "committed resource"; the
  // distribution + shape of edges shows where the room is pulling.
  const radialData = useMemo(() => buildRadialData(actions), [actions]);

  // Keep the old force-directed graph helpers around (toFlow, Legend,
  // NODE_TYPES, etc.) so we can A/B if needed — but suppress the unused
  // warnings since the radial view is now the default.
  void toFlow;
  void Legend;
  void NODE_TYPES;
  void ReactFlow;
  void ReactFlowProvider;
  void Background;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">DIAGNOSTICS · TEAM CAPACITY</div>
          <h1>Team capacity</h1>
        </div>
        <div style={headActions}>
          <span style={refreshMeta}>
            {loading
              ? "Loading…"
              : refreshedAt
                ? `Refreshed ${refreshedAt} · auto-refresh 30s`
                : ""}
          </span>
          <button onClick={() => void load()} style={refreshBtn} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <p style={lede}>
        Live shape of committed resources. The outer ring is every (KPI ×
        category) slot — SYM at 12 o'clock, supply (S1–S3) anchoring the
        left arc, demand (D1–D3) anchoring the right. People sit inside,
        positioned at the centroid of their commitments. Edge thickness
        tracks active workload; thick braids into one perimeter slot tell
        you where the room is pulling, faint slots tell you where it
        isn't. Hover a person to see their fan; hover a slot to see who's
        on it. Auto-refreshes every 30 seconds.
      </p>

      {error && <div style={errorBar}>Failed to load — {error}</div>}

      <div style={layout}>
        <div style={graphPane}>
          <RadialCapacity data={radialData} />
        </div>

        <aside style={statsPane}>
          <StatsRow label="Headcount" value={String(graph.totals.people)} />
          <StatsRow label="Commitments" value={String(graph.totals.actions)} />
          <StatsRow label="Documents" value={String(graph.totals.docs)} />
          <StatsRow label="Notes" value={String(graph.totals.notes)} />

          <div style={panelHead}>HEAVY LOAD · 3+ items</div>
          {graph.heavyLoad.length === 0 ? (
            <div style={panelEmpty}>No one carries 3+ items right now.</div>
          ) : (
            <ul style={panelList}>
              {graph.heavyLoad.map((p) => (
                <li key={p.id} style={panelItem}>
                  <span style={panelName}>{p.label}</span>
                  <span style={panelChips}>
                    {p.load!.commitments > 0 && <span style={chip("cmt")}>{p.load!.commitments}c</span>}
                    {p.load!.documents > 0 && <span style={chip("doc")}>{p.load!.documents}d</span>}
                    {p.load!.notes > 0 && <span style={chip("note")}>{p.load!.notes}n</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div style={panelHead}>KPI OVERLAPS · 2+ owners</div>
          {graph.topOverlaps.length === 0 ? (
            <div style={panelEmpty}>No KPI has multiple committed owners yet.</div>
          ) : (
            <ul style={panelList}>
              {graph.topOverlaps.map(({ node, ownerCount, owners }) => (
                <li key={node.id} style={panelItem}>
                  <span style={kpiChip(node.side)}>{node.sub}</span>
                  <span style={overlapOwners} title={owners.join(", ")}>
                    {ownerCount} · {owners.slice(0, 3).join(", ")}
                    {owners.length > 3 ? ` +${owners.length - 3}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </>
  );
}

function Legend() {
  return (
    <div style={legendBox}>
      <div style={legendHead}>LEGEND</div>
      <div style={legendGrid}>
        <LegendDot color="#6f63d6" label="Person" />
        <LegendDot color="#534ab7" label="KPI · supply" />
        <LegendDot color="#d96b9c" label="KPI · demand" />
        <LegendDot color="#5ec98c" label="Category" />
        <LegendDot color="#d4b876" label="Document" />
        <div style={{ height: 6 }} />
        <LegendLine color="#7ad19f" label="Person owns KPI" />
        <LegendLine color="#5ec98c" label="Person owns category" />
        <LegendLine color="#3a8fb7" dashed label="KPI ↔ category (system)" />
        <LegendLine color="#d99c3a" label="Doc ↔ category" />
        <LegendLine color="#7a7a8a" dashed label="Person uploads / notes" />
        <LegendLine color="#c44a4a" label="Blocked" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}66` }} />
      <span style={{ fontSize: 10, color: "#c7c7d4" }}>{label}</span>
    </div>
  );
}

function LegendLine({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 18, height: 0,
          borderTop: `1.5px ${dashed ? "dashed" : "solid"} ${color}`,
        }}
      />
      <span style={{ fontSize: 10, color: "#c7c7d4" }}>{label}</span>
    </div>
  );
}

function StatsRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={statRow}>
      <span style={statLabel}>{label}</span>
      <span style={statValue}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const headActions: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12 };
const refreshMeta: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em",
  color: "var(--ink-faint)", textTransform: "uppercase",
};
const refreshBtn: React.CSSProperties = {
  padding: "7px 14px", border: "1px solid var(--edge)",
  background: "transparent", color: "var(--ink)",
  fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 700, borderRadius: 4, cursor: "pointer",
};
const lede: React.CSSProperties = {
  maxWidth: 940, fontSize: 13, color: "var(--ink-dim)",
  lineHeight: 1.6, marginBottom: 14,
};
const errorBar: React.CSSProperties = {
  marginBottom: 14, padding: "8px 12px", border: "1px solid var(--coral)",
  borderRadius: 4, color: "var(--coral)", background: "rgba(196,74,74,0.08)", fontSize: 12,
};
const layout: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr 260px", gap: 14, alignItems: "stretch",
};
const graphPane: React.CSSProperties = {
  width: "100%", height: 740, border: "1px solid var(--edge)",
  borderRadius: 8, background: "radial-gradient(circle at 50% 50%, #15171f 0%, #0b0c12 70%)",
  overflow: "hidden", position: "relative",
};
const legendBox: React.CSSProperties = {
  position: "absolute", left: 10, bottom: 10,
  padding: "8px 10px", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 4, background: "rgba(15,17,25,0.75)",
  backdropFilter: "blur(4px)",
};
const legendHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: "0.16em",
  fontWeight: 700, color: "#7a7a8a", marginBottom: 6,
};
const legendGrid: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 14px",
};
const statsPane: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 8 };
const statRow: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "baseline",
  padding: "8px 10px", border: "1px solid var(--edge)",
  borderRadius: 4, background: "var(--panel-2, rgba(255,255,255,0.02))",
};
const statLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
  color: "var(--ink-faint)", textTransform: "uppercase", fontWeight: 700,
};
const statValue: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: "var(--ink)" };
const panelHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
  fontWeight: 700, color: "var(--ink-faint)", textTransform: "uppercase",
  marginTop: 6, marginBottom: 4,
};
const panelEmpty: React.CSSProperties = {
  padding: "8px 10px", border: "1px dashed var(--edge)",
  borderRadius: 4, fontSize: 11, color: "var(--ink-faint)",
};
const panelList: React.CSSProperties = {
  listStyle: "none", padding: 0, margin: 0,
  display: "flex", flexDirection: "column", gap: 3,
};
const panelItem: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 6, padding: "6px 8px", border: "1px solid var(--edge)",
  borderRadius: 4, background: "rgba(0,0,0,0.18)", fontSize: 11,
};
const panelName: React.CSSProperties = { fontWeight: 600, color: "var(--ink)" };
const panelChips: React.CSSProperties = { display: "flex", gap: 4 };
function chip(kind: "cmt" | "doc" | "note"): React.CSSProperties {
  const palette: Record<string, string> = { cmt: "#5ec98c", doc: "#3aa8d9", note: "#d99c3a" };
  const color = palette[kind] ?? "#888";
  return {
    fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700,
    color, padding: "1px 5px", border: `1px solid ${color}55`,
    borderRadius: 3, background: `${color}22`,
  };
}
const overlapOwners: React.CSSProperties = {
  fontSize: 10, color: "var(--ink-dim)",
  textAlign: "right", overflow: "hidden", textOverflow: "ellipsis",
};
function kpiChip(side?: "supply" | "demand" | "sym"): React.CSSProperties {
  const color = side === "supply" ? "#8a7df5" : side === "demand" ? "#e89db6" : "#bababa";
  return {
    fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
    color: "white", padding: "1px 6px", borderRadius: 3, background: color,
    letterSpacing: 0.5, flexShrink: 0,
  };
}
